/*
  Búsqueda por voz con Web Speech API.

  Estrategia de compatibilidad:
  - Chrome/Edge/Safari (macOS 14.1+, iOS 14.5+) y Chrome Android: se muestra el micro.
  - Firefox y navegadores sin la API: el botón permanece oculto; el usuario puede
    dictar igualmente con el dictado del sistema (teclado iOS/Gboard, macOS, Windows)
    al enfocar el campo de búsqueda.
  - Chromium sin servicio de voz (p. ej. Brave): la API existe pero falla en tiempo
    de ejecución con 'network'/'service-not-allowed'; al primer fallo de ese tipo el
    micro se oculta para no dejar un botón muerto.
*/
class VoiceSearchButton extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    const Recognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!Recognition) return;

    this.button = this.querySelector('button');
    const field = this.closest('.field');
    this.input = field ? field.querySelector('input[type="search"]') : null;
    if (!this.button || !this.input) return;

    this.isListening = false;

    this.recognition = new Recognition();
    this.recognition.lang = document.documentElement.lang || 'es-ES';
    this.recognition.interimResults = false;
    this.recognition.continuous = false;
    this.recognition.maxAlternatives = 1;

    this.recognition.addEventListener('result', this.onResult.bind(this));
    this.recognition.addEventListener('start', () => this.setListening(true));
    this.recognition.addEventListener('end', () => this.setListening(false));
    this.recognition.addEventListener('error', this.onError.bind(this));

    this.button.addEventListener('click', this.toggle.bind(this));
    this.button.hidden = false;
  }

  toggle() {
    if (this.isListening) {
      this.recognition.stop();
      return;
    }
    try {
      this.recognition.start();
    } catch (error) {
      // InvalidStateError si ya hay una sesión arrancando: la paramos y listo
      this.recognition.stop();
    }
  }

  setListening(isListening) {
    this.isListening = isListening;
    this.button.classList.toggle('voice-search__button--listening', isListening);
  }

  onError(event) {
    this.setListening(false);
    // El navegador no puede dictar (sin permiso, sin servicio de voz o sin red):
    // ocultamos el micro y dejamos como vía el dictado del teclado del sistema.
    const fatalErrors = ['not-allowed', 'service-not-allowed', 'network'];
    if (fatalErrors.includes(event.error)) {
      this.button.hidden = true;
      this.input.focus();
    }
  }

  onResult(event) {
    const transcript = event.results[0][0].transcript;
    this.input.value = transcript;
    this.input.dispatchEvent(new Event('input', { bubbles: true }));
    this.input.focus();

    // Con la búsqueda predictiva activa, el evento 'input' de arriba abre el
    // desplegable de resultados. Si está desactivada no hay desplegable que
    // reaccione, así que enviamos el formulario para que la voz sí ejecute la
    // búsqueda en lugar de dejar el término escrito sin hacer nada.
    if (!this.closest('predictive-search')) {
      const form = this.input.form;
      if (form) {
        if (typeof form.requestSubmit === 'function') {
          form.requestSubmit();
        } else {
          form.submit();
        }
      }
    }
  }
}

if (!customElements.get('voice-search-button')) {
  customElements.define('voice-search-button', VoiceSearchButton);
}
